// src/data/regionThemes.js
// ─────────────────────────────────────────────────────────────────────────────
// Single source of truth for every region's visual identity.
//
// Used by:
//   • src/vfx/background.js   — gradient, nebula, stars, orb, frame, particles
//   • src/scenes/MapScene.js  — nebula effect in level-select modal
//   • src/scenes/LevelScene.js — end-of-level modal tint
//   • src/ui/hud.js           — future accent overrides per region
//
// Fields per region
// ─────────────────
//  primary      — dominant accent (buttons, glows, key UI elements)
//  secondary    — lighter highlight (text shimmer, secondary particles)
//  glow         — deep saturated tone (orb shadow, portal tint)
//  particle     — particle preset key: 'embers'|'bubbles'|'leaves'|'sparks'|'petals'
//  nebula       — base colour for the MapScene cloud / nebula overlay
//  starColor    — tint applied to background star rectangles
//  bgTop        — gradient top colour (screen top)
//  bgBottom     — gradient bottom colour (screen bottom, more saturated)
//  boardGlow    — corner-bracket + floor-line colour
// ─────────────────────────────────────────────────────────────────────────────

/** Nome oficial do mundo. Usar em TitleScene, créditos, etc. */
export const WORLD_NAME = "Prismara";

export const RegionThemes = {

  // ── 🌋 Ignarion — Reino da Lava Viva ────────────────────────────────────
  volcano: {
    name:       'Ignarion',
    primary:    '#ff4500',
    secondary:  '#fbbf24',
    glow:       '#7f1d1d',
    particle:   'embers',
    nebula:     '#7f1d1d',
    starColor:  '#ffd4a0',
    bgTop:      '#0c0306',   // near-black with faint red warmth
    bgBottom:   '#260d06',   // dark crimson — clearly distinct
    boardGlow:  '#ff4500',
    bgKey:      'bg-ignarion',
  },

  // ── 🌊 Hydrallis — Império das Marés ────────────────────────────────────
  ocean: {
    name:       'Hydrallis',
    primary:    '#06b6d4',
    secondary:  '#a5f3fc',
    glow:       '#164e63',
    particle:   'bubbles',
    nebula:     '#164e63',
    starColor:  '#a0d4ff',
    bgTop:      '#030810',   // near-black, blue-tinted
    bgBottom:   '#071728',   // deep dark ocean blue
    boardGlow:  '#06b6d4',
    bgKey:      'bg-hydrallis',
  },

  // ── 🌿 Terravael — Floresta Primordial ──────────────────────────────────
  earth: {
    name:       'Terravael',
    primary:    '#22c55e',
    secondary:  '#a3e635',
    glow:       '#14532d',
    particle:   'leaves',
    nebula:     '#14532d',
    starColor:  '#d0ffb0',
    bgTop:      '#030804',   // near-black, green-tinted
    bgBottom:   '#081508',   // dark forest green
    boardGlow:  '#22c55e',
    bgKey:      'bg-terraevel',
  },

  // ── ⚡ Voltaryn — Metrópole Neon ─────────────────────────────────────────
  storm: {
    name:       'Voltaryn',
    primary:    '#facc15',
    secondary:  '#a78bfa',
    glow:       '#312e81',
    particle:   'sparks',
    nebula:     '#312e81',
    starColor:  '#ffffa0',
    bgTop:      '#080808',   // neutral dark
    bgBottom:   '#0d0a1e',   // dark indigo-blue, stormy
    boardGlow:  '#facc15',
    bgKey:      'bg-voltrix',
  },

  // ── 🧠 Aetherion — Templo Psíquico ─────────────────────────────────────
  psychic: {
    name:       'Aetherion',
    primary:    '#d946ef',
    secondary:  '#f0abfc',
    glow:       '#4a044e',
    particle:   'petals',
    nebula:     '#4a044e',
    starColor:  '#ffa0ff',
    bgTop:      '#090408',   // near-black, faint purple
    bgBottom:   '#160812',   // deep dark magenta-purple
    boardGlow:  '#d946ef',
    bgKey:      'bg-aetherion',
  },

};

// ── Helper: parse a CSS hex colour → { r, g, b } 0-255 ───────────────────
export function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

// ── Helper: CSS hex → Phaser integer (0xRRGGBB) ───────────────────────────
export function hexToPhaser(hex) {
  return parseInt(hex.replace('#', ''), 16);
}
